import { createServerFn } from "@tanstack/react-start";

type SignupInput = {
  email: string;
  full_name?: string | null;
  volunteer_track?: string | null;
  why_help?: string | null;
  athlete_ref?: string | null;
};

const TRACKS = new Set(["remote", "onsite", "both"]);

/**
 * Auto-approves an event helper as a Warrior Volunteer. Only callable for an
 * email that already has a confirmed event signup, which is our proof they
 * really claimed a spot with us. Runs the exact same provisioning and emails
 * as an admin approval.
 */
export const selfSignupFromClaim = createServerFn({ method: "POST" })
  .inputValidator((data: SignupInput) => {
    const email = String(data?.email ?? "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 200) {
      throw new Error("Enter a valid email address.");
    }
    const track = String(data?.volunteer_track ?? "onsite");
    return {
      email,
      full_name: (data?.full_name ?? "").toString().trim().slice(0, 120) || null,
      volunteer_track: TRACKS.has(track) ? track : "onsite",
      why_help: (data?.why_help ?? "").toString().trim().slice(0, 1000) || null,
      athlete_ref: /^[A-Za-z0-9]{8,16}$/.test(String(data?.athlete_ref ?? "").trim()) ? String(data?.athlete_ref).trim() : null,
    };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const signup = await supabaseAdmin
      .from("event_signups")
      .select("id, full_name, phone, t1d_connection, claim_answers, created_at")
      .ilike("email", data.email)
      .eq("status", "confirmed")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (signup.error) throw new Error(signup.error.message);
    if (!signup.data) {
      throw new Error("We could not find a claimed spot for that email address.");
    }

    // Already a volunteer? Nothing to do, and no duplicate welcome email.
    const existingProfile = await supabaseAdmin
      .from("volunteer_profiles")
      .select("id")
      .ilike("email", data.email)
      .maybeSingle();
    if (existingProfile.data) {
      return { ok: true as const, alreadyVolunteer: true };
    }

    const { toProperCase } = await import("@/lib/proper-case");
    const fullName = toProperCase(data.full_name || signup.data.full_name || data.email);
    const answers = (signup.data.claim_answers ?? {}) as Record<string, unknown>;
    const shirtSize = typeof answers.shirt_size === "string" ? answers.shirt_size : null;
    const now = new Date().toISOString();

    const application = await supabaseAdmin
      .from("volunteer_applications")
      .insert({
        full_name: fullName,
        email: data.email,
        phone: signup.data.phone,
        why_help: data.why_help ?? "Signed up after claiming an event spot.",
        t1d_connection: signup.data.t1d_connection,
        volunteer_track: data.volunteer_track,
        status: "approved",
        reviewed_at: now,
        notes: "Auto-approved from an event claim.",
        email_consent: true,
        email_consent_at: now,
        email_consent_source: "event_claim_signup",
        athlete_ref: data.athlete_ref,
      })
      .select("*")
      .single();
    if (application.error) throw new Error(application.error.message);

    try {
      const { reportSignupInBackground } = await import("@/lib/athlete-referral.server");
      reportSignupInBackground(application.data.id, data.athlete_ref, "email");
    } catch {
      // Referral reporting must never break sign-up.
    }

    const { provisionApprovedVolunteer } = await import("@/lib/volunteer-provision.server");
    const result = await provisionApprovedVolunteer(application.data);

    if (shirtSize) {
      await supabaseAdmin
        .from("volunteer_profiles")
        .update({ shirt_size: shirtSize })
        .eq("user_id", result.userId);
    }

    return { ok: true as const, alreadyVolunteer: false, welcomeEmailSent: result.welcomeEmailSent };
  });
